import React, { useState, useEffect } from 'react';
import { ChevronRight, Circle, Search } from 'lucide-react';
import { categoriesApi, VintedCategory, CategorySearchResult } from '../services/categoriesApi';

interface CategorySelectorProps {
  selectedCategoryId?: string;
  onCategorySelect: (categoryId: string, categoryPath: string) => void;
  compact?: boolean;
}

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  selectedCategoryId,
  onCategorySelect,
  compact = false
}) => {
  const [categories, setCategories] = useState<VintedCategory[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<VintedCategory[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CategorySearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const results = await categoriesApi.search(searchQuery);
        setSearchResults(results);
      } catch (err) {
        console.error('Erreur recherche:', err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await categoriesApi.getTree();
      setCategories(data.catalogs || data.categories || []);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
      setError('Impossible de charger les catégories. Vérifie que le backend est démarré.');
    } finally {
      setLoading(false);
    }
  };

  const buildPath = (items: VintedCategory[]): string =>
    items.map(c => c.title).join(' > ');

  const handleCategoryClick = (category: VintedCategory, columnIndex: number, pathPrefix: string) => {
    const hasChildren = category.children && category.children.length > 0;
    const currentPath = pathPrefix ? `${pathPrefix} > ${category.title}` : category.title;

    // Toujours valider la sélection (n'importe quel niveau : Femmes, Chaussures, Ballerines...)
    onCategorySelect(category.id, currentPath);
    setSearchQuery('');

    if (hasChildren) {
      const newBreadcrumb = [...breadcrumb.slice(0, columnIndex), category];
      setBreadcrumb(newBreadcrumb);
    }
  };

  const handleSearchResultClick = (result: CategorySearchResult) => {
    const path = result.path || (result as any).full_path || result.title;
    onCategorySelect(result.id, path);
    setSearchQuery('');
  };

  // Construire les colonnes : [racine, enfants breadcrumb[0], enfants breadcrumb[1], ...]
  const columns: VintedCategory[][] = [categories];
  for (const parent of breadcrumb) {
    columns.push(parent.children || []);
  }

  const isCategoryInPath = (category: VintedCategory, colIndex: number): boolean => {
    if (colIndex === 0) return breadcrumb[0]?.id === category.id;
    return breadcrumb[colIndex]?.id === category.id;
  };

  const renderBubble = (category: VintedCategory, columnIndex: number, pathPrefix: string) => {
    const hasChildren = category.children && category.children.length > 0;
    const isSelected = isCategoryInPath(category, columnIndex);
    const isFinalSelected = category.id === selectedCategoryId;

    return (
      <button
        key={category.id}
        type="button"
        onClick={() => handleCategoryClick(category, columnIndex, pathPrefix)}
        className={`
          flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 transition-all text-sm font-medium
          whitespace-nowrap
          ${isSelected || isFinalSelected
            ? 'bg-gray-200 border-gray-300 text-gray-900'
            : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 hover:bg-gray-50'
          }
        `}
      >
        <span>{category.title}</span>
        {hasChildren ? (
          <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
        ) : (
          <Circle size={12} className="text-gray-500 flex-shrink-0" />
        )}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-gray-500">
        Chargement des catégories...
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadCategories}
          className="px-4 py-2 bg-cyan-500 text-white rounded-xl hover:bg-cyan-600 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      {/* Barre de recherche */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Rechercher une catégorie..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
        />
      </div>

      {/* Résultats de recherche */}
      {searchQuery.length >= 2 && (
        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl bg-gray-50">
          {searchResults.length > 0 ? (
            <div className="p-3">
              <p className="text-xs text-gray-500 mb-3">
                {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                {searchResults.map(result => (
                  <button
                    key={result.id}
                    type="button"
                    onClick={() => handleSearchResultClick(result)}
                    title={result.path || (result as any).full_path || result.title}
                    className={`
                      px-4 py-2 rounded-2xl border-2 transition-all text-sm font-medium
                      ${result.id === selectedCategoryId
                        ? 'bg-gray-200 border-gray-300 text-gray-900'
                        : 'bg-white border-gray-300 hover:border-cyan-400 hover:bg-cyan-50'
                      }
                    `}
                  >
                    {result.title}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500 text-sm">
              Aucun résultat pour &quot;{searchQuery}&quot;
            </div>
          )}
        </div>
      )}

      {/* Colonnes de catégories en bulles - défilement horizontal */}
      {searchQuery.length < 2 && (
        <div className="category-columns-scroll overflow-x-auto overflow-y-hidden pb-2 -mx-1">
          <div className="flex gap-4 min-w-max px-1">
            {columns.map((columnCats, colIndex) => {
              if (columnCats.length === 0) return null;

              const pathPrefix = colIndex === 0 ? '' : buildPath(breadcrumb.slice(0, colIndex));

              return (
                <div
                  key={colIndex}
                  className="flex flex-col gap-2 min-w-[140px] flex-shrink-0"
                >
                  {columnCats.map(cat =>
                    renderBubble(cat, colIndex, pathPrefix)
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {searchQuery.length < 2 && categories.length === 0 && (
        <div className="py-8 text-center text-gray-500 text-sm">
          Aucune catégorie disponible
        </div>
      )}
    </div>
  );
};
